import * as React from "react";
import classNames from "classnames";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { DatePicker as MUIDatePicker } from "material-ui-pickers";
import { startOfMonth, endOfMonth, addMonths, subMonths } from "date-fns";
import { getOccupiedDaysByRange, OccupiedDay } from "../../api/occupied-days";
import { updateOccupiedDays, openEditAvailabilitiesDialog, updateAvailabilityDatepickerId } from "../../actions/store/app-actions";
import { updateAvailability } from "../../actions/occupied-day-actions";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { ConnectedSheet } from "../sheet";

interface State {
	selectedDate: Date;
}

type Props =
	WithStyles<typeof styles> &
	InjectedTranslateProps &
	ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps>;

export class Settings extends React.Component<Props, State> {
	private inputRef: HTMLInputElement = null;

	public state: State = {
		selectedDate: new Date()
	};

	public async componentDidMount() {
		const { selectedDate } = this.state;

		await this.getOccupiedDaysForMonth(selectedDate);
		this.open();
	}

	public componentWillReceiveProps(nextProps: Props) {}

	public componentDidUpdate(prevProps: Props) {
		if (prevProps.datepickerId !== this.props.datepickerId) {
			this.open();
		}
	}

	private getOccupiedDaysForMonth = async (date: Date) => {
		const { updateOccupiedDays } = this.props;

		try {
			const from = startOfMonth(date);
			const to = endOfMonth(date);

			const occupiedDays = await getOccupiedDaysByRange(from, to);
			updateOccupiedDays(occupiedDays);
		} catch (e) {
			console.error(e);
		}
	};

	public open = async () => {
		this.inputRef.click();
	};

	private handleInputRef = (ref: HTMLInputElement) => {
		this.inputRef = ref;
	};

	private handleDialogShown = () => {
		const { selectedDate } = this.state;
		const { classes } = this.props;
		const modalClass = classes.datepickerModal;
		const attribute = "data-has-custom-events";

		const modalElement = document.querySelector(`.${modalClass}`);
		if(!modalElement || modalElement.getAttribute(attribute) === "true") {
			return;
		}
	
		modalElement.setAttribute(attribute, "true");

		const prevButton = document.querySelector(
			`.${modalClass} > div > div > div > div > button:first-child`
		) as HTMLButtonElement;
		const nextButton = document.querySelector(
			`.${modalClass} > div > div > div > div > button:last-child`
		) as HTMLButtonElement;

		let p = false;
		let n = false;
		let monthOffset = 0;

		prevButton.addEventListener("click", async event => {
			if (!p) {
				event.stopPropagation();

				try {
					monthOffset--;
					await this.getOccupiedDaysForMonth(
						subMonths(selectedDate, monthOffset * -1)
					);
				} catch (e) {}

				p = true;
				prevButton.click();
			} else {
				p = false;
			}
		});

		nextButton.addEventListener("click", async event => {
			if (!n) {
				event.stopPropagation();

				try {
					monthOffset++;
					await this.getOccupiedDaysForMonth(
						addMonths(selectedDate, monthOffset)
					);
				} catch (e) {}
				n = true;
				nextButton.click();
			} else {
				n = false;
			}
		});
	};

	private handleChange = async (date: Date) => {
		const {  occupiedDays, updateAvailabilityDatepickerId } = this.props;

		this.setState({ selectedDate: date });
		
		let isAvailable = false;

		occupiedDays.forEach(day => { 
			if (date.getTime() === day.date.getTime()) {
				isAvailable = day.availability;
			}
		});

		// neuer Wert
		isAvailable = !isAvailable;

		await updateAvailability(date, isAvailable);

		updateAvailabilityDatepickerId();
		this.open();
	};

	private handlePopup = () => {
		const { openEditAvailabilitiesDialog } = this.props;

		openEditAvailabilitiesDialog();
	};

	private renderDay = (
		day: Date,
		selectedDate: Date,
		dayInCurrentMonth: boolean,
		dayComponent: React.ReactElement<{}>
	) => {
		const { classes, occupiedDays } = this.props;

		const todayMidnight = new Date();
		todayMidnight.setHours(0, 0, 0, 0);
	
		let isDisabled = day < todayMidnight;
		let isAvailable = false;

		occupiedDays.forEach(occupiedDay => { 
			if (day.getTime() === occupiedDay.date.getTime()) {
				isAvailable = occupiedDay.availability;
			}
		});

		return (
			<div className={classNames(classes.dayWrapper, {
				[classes.dayWrapperIsAvailable]: isAvailable,
				[classes.dayWrapperIsDisabled]: isDisabled || (dayComponent.props as any).hidden,
			})}>
				{dayComponent}
			</div>
		);
	};

	public render() {
		const { classes, datepickerId, t } = this.props;
		const { selectedDate } = this.state;

		return (
			<ConnectedSheet maxWidth={1200}>
				<Typography className={classes.sectionTitle}>
					Verfügbarkeiten
				</Typography>
				<div className={classes.datepickerWrapper}>
					<div className={classes.datepickerWrapperCol}>
						<div id="availabilitiesDatepickerLocation" className={classes.datepickerContainer} ></div>
					</div>
					<div className={classes.datepickerWrapperCol}>
						<div className={classes.cardContainer}>
							<Typography className={classes.sectionTitle}>
								Tools
							</Typography>
							<Button onClick={this.handlePopup} className={classes.button} variant="contained" color="primary">Zeitspanne bearbeiten</Button>
						</div>
					</div>
				</div>
				<div className={classes.datepickerInput}>
					<MUIDatePicker
						key={datepickerId}
						autoOk
						disablePast
						okLabel={""}
						cancelLabel={""}
						inputRef={(this.handleInputRef)}
						onChange={this.handleChange}
						renderDay={this.renderDay}
						dialogContentClassName={classes.datepickerModalControls}
						value={selectedDate}
						DialogProps={{
							container: document.getElementById('availabilitiesDatepickerLocation'),
							className: classes.datepickerModal,
							onEntered: this.handleDialogShown,
							onExiting: () => {},
							onExited: () => {},
						}}
					/>
				</div>
			</ConnectedSheet>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	occupiedDays: state.occupiedDays,
	datepickerId: state.availabilityDatepickerId
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	updateOccupiedDays: (occupiedDays: OccupiedDay[]) =>
		dispatch(updateOccupiedDays(occupiedDays)),
	openEditAvailabilitiesDialog: () => 
		dispatch(openEditAvailabilitiesDialog()),
	updateAvailabilityDatepickerId: () => 
		dispatch(updateAvailabilityDatepickerId()),
});

const styles = (theme: Theme) =>
	createStyles({
		datepickerWrapper: {
			display: "flex",
			gap: "40px",
			alignItems: "center",
			justifyContent: "center",

			"@media (max-width: 600px)": {
				flexDirection: "column",
				gap: "0px",
			}
		},
		datepickerWrapperCol: {
			flex: 1,
		},
		cardContainer: {
			"@media (max-width: 600px)": {
				paddingBottom: 48
			}
		},
		datepickerContainer: {
			display: "flex",
			justifyContent: "right"
		},
		dayWrapper: {
			background: "#ebb7b7",
			position: "relative",
			'& > button[tabindex="0"]': {
				color: "black",
				background: "transparent",
				fontWeight: "normal"
			},
			'& > button[tabindex="0"]:hover': {
				color: "black",
				background: "transparent"
			}
		},
		dayWrapperIsAvailable: {
			background: "#aec7ae",
		},
		dayWrapperIsDisabled: {
			background: "white"
		},
		container: {},
		mainTitle: {
			marginBottom: 25
		},
		sectionTitle: {
			fontSize: "1.2rem",
			fontWeight: 500,
			marginBottom: 10,
			marginTop: 0
		},
		button: {
			marginTop: 0,
		},
		datepickerModal: {
			position: "static",
			top: "initial",
			right: "initial",
			left: "initial",
			bottom: "initial",
			zIndex: 1,
			"& > div:first-child": {
				display: "none"
			},
			"& > div": {
				marginRight: 0,
				marginLeft: 0,
				boxShadow:
					"0px 1px 5px 0px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.12)",
			}
		},
		datepickerModalControls: {
			"& + div": {
				display: "none"
			}
		},
		datepickerInput: {
			display: "none"
		}
	});

export const ConnectedAvailabilities = translate("translations")(
	withStyles(styles)(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(Settings)
	)
);
